import clsx from "clsx";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { useAccordion } from "mds/hooks/use-accordion";
import { ChevronDownIcon, ChevronUpIcon } from "mds/icons";

type AccordionTitleProps = {
  children: ReactNode;
  title: string | ReactNode;
  iconPosition?: "start" | "end";
  startOpen?: boolean;
  showBorder?: boolean;
  className?: string;
  headerClassName?: string;
  expandedHeaderClassName?: string;
  collapsedHeaderClassName?: string;
};

/**
 * Displays a title with a chevron icon to the left of it, which acts as an
 * accordion to toggle arbitrary children.
 */
export const AccordionTitle: FC<AccordionTitleProps> = ({
  children,
  title,
  startOpen = false,
  iconPosition = "start",
  showBorder = false,
  className,
  headerClassName,
  expandedHeaderClassName,
  collapsedHeaderClassName,
}) => {
  const { expanded, toggle } = useAccordion(startOpen);

  const icon = expanded ? <ChevronUpIcon /> : <ChevronDownIcon />;
  const iconStart = iconPosition === "start";

  return (
    <div
      className={twMerge(
        "accordion-title cursor-pointer",
        clsx({ "rounded-lg border border-black-tint-90": showBorder }),
        className,
      )}
    >
      <div
        className={twMerge(
          "flex items-center gap-1",
          expanded
            ? expandedHeaderClassName || headerClassName
            : collapsedHeaderClassName || headerClassName,
        )}
        title={expanded ? t("tooltip.collapse") : t("tooltip.expand")}
        onClick={toggle}
      >
        {iconStart && icon}

        <h4 className={clsx("body-m py-2 text-black-tint-20", { "ml-2": iconStart })}>{title}</h4>

        {!iconStart && icon}
      </div>

      {expanded && (
        <div className={clsx("accordion-title-content cursor-default py-0", { "pl-5": iconStart })}>
          {children}
        </div>
      )}
    </div>
  );
};
